(() => {
  if (customElements.get('lc-accessories-slider')) return;

  class LCAccessoriesSlider extends HTMLElement {
    constructor() {
      super();
      this.sectionRoot = this.closest('.lc-accessories') || this;
      this.viewport = this.querySelector('[data-lc-accessories-viewport]');
      this.previousButton = this.sectionRoot.querySelector('[data-lc-accessories-previous]');
      this.nextButton = this.sectionRoot.querySelector('[data-lc-accessories-next]');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplay = this.dataset.autoplay === 'true';
      this.loop = this.dataset.loop === 'true';
      this.autoplaySpeed = Math.max(3000, Number(this.dataset.autoplaySpeed) || 4000);
      this.autoplayTimer = null;
      this.isVisible = false;
      this.isPaused = false;
      this.updateButtons = this.updateButtons.bind(this);
      this.handlePrevious = this.handlePrevious.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.pause = this.pause.bind(this);
      this.resume = this.resume.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);
    }

    connectedCallback() {
      if (!this.viewport || !this.previousButton || !this.nextButton) return;

      this.previousButton.addEventListener('click', this.handlePrevious);
      this.nextButton.addEventListener('click', this.handleNext);
      this.viewport.addEventListener('scroll', this.updateButtons, { passive: true });
      this.addEventListener('mouseenter', this.pause);
      this.addEventListener('mouseleave', this.resume);
      this.addEventListener('focusin', this.pause);
      this.addEventListener('focusout', this.handleFocusOut);
      this.addEventListener('pointerdown', this.pause, { passive: true });
      this.addEventListener('pointerup', this.handlePointerUp, { passive: true });
      window.addEventListener('resize', this.updateButtons, { passive: true });
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      this.reduceMotion.addEventListener?.('change', this.handleMotionChange);

      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.isVisible = entry.isIntersecting && entry.intersectionRatio >= 0.25;
        this.syncAutoplay();
      }, { threshold: [0, 0.25] });
      this.visibilityObserver.observe(this);
      this.updateButtons();
    }

    disconnectedCallback() {
      this.viewport?.removeEventListener('scroll', this.updateButtons);
      this.previousButton?.removeEventListener('click', this.handlePrevious);
      this.nextButton?.removeEventListener('click', this.handleNext);
      this.removeEventListener('mouseenter', this.pause);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('focusin', this.pause);
      this.removeEventListener('focusout', this.handleFocusOut);
      this.removeEventListener('pointerdown', this.pause);
      this.removeEventListener('pointerup', this.handlePointerUp);
      window.removeEventListener('resize', this.updateButtons);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.reduceMotion.removeEventListener?.('change', this.handleMotionChange);
      this.visibilityObserver?.disconnect();
      this.stopAutoplay();
    }

    move(direction) {
      const firstSlide = this.viewport.querySelector('.lc-accessories__item');
      const track = this.viewport.querySelector('.lc-accessories__track');
      if (!firstSlide || !track) return;

      const gap = Number.parseFloat(window.getComputedStyle(track).columnGap) || 0;
      const step = firstSlide.getBoundingClientRect().width + gap;
      const maximum = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      let target = this.viewport.scrollLeft + (step * direction);

      if (this.loop && maximum > 1) {
        if (direction > 0 && this.viewport.scrollLeft >= maximum - 1) target = 0;
        if (direction < 0 && this.viewport.scrollLeft <= 1) target = maximum;
      }

      this.viewport.scrollTo({
        left: Math.max(0, Math.min(target, maximum)),
        behavior: this.reduceMotion.matches ? 'auto' : 'smooth'
      });
    }

    handlePrevious() {
      this.move(-1);
      this.restartAutoplay();
    }

    handleNext() {
      this.move(1);
      this.restartAutoplay();
    }

    pause() {
      this.isPaused = true;
      this.stopAutoplay();
    }

    resume() {
      this.isPaused = false;
      this.syncAutoplay();
    }

    handleFocusOut(event) {
      if (!event.relatedTarget || !this.contains(event.relatedTarget)) this.resume();
    }

    handlePointerUp() {
      this.isPaused = this.matches(':hover') || this.contains(document.activeElement);
      this.syncAutoplay();
    }

    handleVisibilityChange() {
      this.syncAutoplay();
    }

    handleMotionChange() {
      this.syncAutoplay();
    }

    startAutoplay() {
      if (this.autoplayTimer) return;
      this.autoplayTimer = window.setInterval(() => this.move(1), this.autoplaySpeed);
    }

    stopAutoplay() {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.syncAutoplay();
    }

    syncAutoplay() {
      const canScroll = this.viewport && this.viewport.scrollWidth > this.viewport.clientWidth + 1;
      const shouldPlay = this.autoplay
        && this.isVisible
        && !this.isPaused
        && !this.reduceMotion.matches
        && !document.hidden
        && canScroll;

      if (shouldPlay) this.startAutoplay();
      else this.stopAutoplay();
    }

    updateButtons() {
      if (!this.viewport) return;

      const maximum = this.viewport.scrollWidth - this.viewport.clientWidth;
      const canScroll = maximum > 1;
      this.previousButton.disabled = !canScroll || (!this.loop && this.viewport.scrollLeft <= 1);
      this.nextButton.disabled = !canScroll || (!this.loop && this.viewport.scrollLeft >= maximum - 1);
      this.syncAutoplay();
    }
  }

  customElements.define('lc-accessories-slider', LCAccessoriesSlider);
})();
